import {
	ReactElement,
	Ref,
	useCallback,
	useEffect,
	useRef,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { Guid } from "client_legacy/ndaw/guid";
import {
	WeekSlider,
	DateString,
	BusyIndicator,
	DropOver,
	WeekView,
	WeekViewCardStyle,
	Space
} from "client/Components";
import {
	NedapOnsEventKind,
	NedapOnsEventListItem,
	NedapOnsMyScheduleResponseModel,
	NedapOnsResultStatus
} from "client/Api/Workspace/LiveInformation/NedapOns/nedapOnsLiveInformation";
import { InterestedNedapOnsParty, NedapOnsLiveInformationProvider } from "./provider";
import { LiveTileContent } from "../LiveTileContent";
import { EventDetails } from "./EventDetails";
import { useWeekDays, WeekDay } from "client/Hooks";
import { TileRefresh } from "../TileRefresh/TileRefresh";
import { DimensionToRender } from "../../RenderState/common";
import styles from "./NedapOnsMySchedule.module.less";
import { navigateToNedapOnsApp } from "client_legacy/Apps/appNavigation";
import { DestinationWindow } from "client/Api/Apps/common";
import { EMPTY_ARRAY, generateTrackingId } from "client/Misc/utils";
import { TimeZone } from "client/DateAndTime/timezones";
import { formatForDto } from "client/DateAndTime/dateTimeUtils";

const {
	Scrollable,
	NoResults
} = LiveTileContent;

const {
	Card,
	Card: {
		Title,
		Info
	}
} = WeekView;

type LoadingState = {
	isLoading: boolean;
	hasTooManyConsecutiveErrors?: boolean;
	hasGlobalFailure?: boolean;
	response?: NedapOnsMyScheduleResponseModel;
};

export type NedapOnsMyScheduleProps = {
	tabId: Guid;
	connectionId: number;
	provider: NedapOnsLiveInformationProvider;
	dimensionToRender: DimensionToRender;
	destinationWindow: DestinationWindow;
	environmentAppId: number;
	timezone: TimeZone;
	addAnalyticsEvent(): void;
};

export const NedapOnsMySchedule = (props: NedapOnsMyScheduleProps): ReactElement => {
	const {
		tabId,
		connectionId,
		provider,
		dimensionToRender,
		destinationWindow,
		environmentAppId,
		timezone,
		addAnalyticsEvent
	} = props;

	const [date, setDate] = useState<Date>(() => new Date());

	const [loadingState, setLoadingState] = useState<LoadingState>({
		isLoading: true
	});

	const days: readonly WeekDay[] = useWeekDays(
		date,
		"culture",
		timezone
	);

	if (days.length === 0) {
		throw new Error("Week does not contain any day.");
	}
	const startOfWeek: Date = days[0].date;

	const isTodayButtonAvailable: boolean = dimensionToRender.dimension.columnSpan === 8;

	useEffect(
		() => {
			const party: InterestedNedapOnsParty = {
				partyId: tabId,
				resourceId: {
					connectionId,
					date: formatForDto(startOfWeek, timezone)
				},
				informAboutGlobalRetrievalFailure: (): void => {
					setLoadingState((x) => ({
						...x,
						isLoading: false,
						hasTooManyConsecutiveErrors: false,
						hasGlobalFailure: true,
						response: undefined
					}));
				},
				informAboutConsecutiveErrors: (): void => {
					setLoadingState((x) => ({
						...x,
						isLoading: false,
						hasTooManyConsecutiveErrors: true
					}));
				},
				updateMySchedule: (response: NedapOnsMyScheduleResponseModel): void => {
					setLoadingState((x) => ({
						...x,
						isLoading: false,
						hasTooManyConsecutiveErrors: false,
						hasGlobalFailure: false,
						response
					}));
				}
			};

			provider.register(party, { mySchedule: true });

			return () => {
				provider.unregister(party);
			};
		},
		[
			connectionId,
			provider,
			startOfWeek,
			tabId,
			timezone
		]
	);

	const changeDateHandler = (value: Date): void => {
		setDate(value);

		setLoadingState((x) => ({
			...x,
			isLoading: true
		}));
	};

	const tileRefreshHandler = (): void => {
		setLoadingState((x) => ({
			...x,
			isLoading: true
		}));
		provider.forceReload();
	};

	const {
		isLoading,
		hasTooManyConsecutiveErrors,
		hasGlobalFailure,
		response
	} = loadingState;

	const hasError: boolean = hasGlobalFailure || response?.status !== NedapOnsResultStatus.Success;
	const hasItems: boolean = (response?.items?.length ?? 0) > 0;

	const navigationHandler = useCallback(
		(): void => {
			addAnalyticsEvent();

			navigateToNedapOnsApp({
				id: environmentAppId,
				destinationWindow: destinationWindow,
				view: "roster"
			});
		},
		[
			addAnalyticsEvent,
			destinationWindow,
			environmentAppId
		]
	);

	const renderContent = (): ReactElement => {
		if (isLoading) {
			return <BusyIndicator />;
		}

		if (hasTooManyConsecutiveErrors) {
			return <TileRefresh onRefresh={tileRefreshHandler} />;
		}

		if (hasError) {
			return (
				<NoResults
					trackingId="has-error"
					icon="gear"
					title={localize("_UnableToRetrieveItems")}
					message={localize("_WeWereUnableToRetrieveTheItems")}
					onRetry={tileRefreshHandler}
				/>
			);
		}

		if (!hasItems) {
			return (
				<NoResults
					trackingId="no-shift"
					icon="calendar"
					title={localize("_NoShiftsThisWeek")}
					message={localize("_ThereAreNoWorkShiftsForThisWeek")}
				/>
			);
		}

		return (
			<Scrollable>
				<WeekView
					days={days}
					items={response?.items ?? EMPTY_ARRAY}
					itemKey={getEventKey}
					startDateTimeRetriever={startDateTimeRetriever}
					endDateTimeRetriever={endDateTimeRetriever}
					timezone={timezone}
				>
					{(x): ReactElement => (
						<Event
							connectionId={connectionId}
							item={x}
							timezone={timezone}
						/>
					)}
				</WeekView>
			</Scrollable>
		);
	};

	return(
		<>
			<LiveTileContent.Content>
				<LiveTileContent.ContentStack>
					<div className={styles.sliderContainer}>
						<WeekSlider
							startDate={startOfWeek}
							onChange={changeDateHandler}
							isTodayButtonAvailable={isTodayButtonAvailable}
							trackingId="nedap-ons-my-schedule-week-view"
							timezone={timezone}
						/>
					</div>
					<LiveTileContent.BusyIndicatorContainer>
						{renderContent()}
					</LiveTileContent.BusyIndicatorContainer>
				</LiveTileContent.ContentStack>
			</LiveTileContent.Content>

			<LiveTileContent.Footer
				name={localize("_MySchedule")}
				onClick={navigationHandler}
			/>
		</>
	);
};
NedapOnsMySchedule.displayName = "NedapOnsMySchedule";

type EventProps = {
	connectionId: number;
	item: NedapOnsEventListItem;
	timezone: TimeZone;
};

const Event = (props: EventProps): ReactElement => {
	const {
		connectionId,
		item,
		timezone
	} = props;

	const [isViewingDetails, setIsViewingDetails] = useState<boolean>(false);

	const ref: Ref<HTMLDivElement> = useRef<HTMLDivElement>(null);

	return (
		<>
			<Card
				onClick={() => setIsViewingDetails(true)}
				ref={ref}
				isSelected={isViewingDetails}
				style={getCardStyle(item.kind)}
				title={item.title}
				trackingId={generateTrackingId("event", `|${item.start}`)}
			>
				<Title>
					{ item.title }
				</Title>
				<Info>
					<DateString
						value={item.start}
						format="T"
						timezone={timezone}
					/>
					<Space />-<Space />
					<DateString
						value={item.end}
						format="T"
						timezone={timezone}
					/>
				</Info>
			</Card>

			{ isViewingDetails && ref.current && (
				<DropOver
					target={ref.current}
					onClose={() => setIsViewingDetails(false)}
				>
					<EventDetails
						connectionId={connectionId}
						item={item}
						variant={getCardStyle(item.kind)}
						onClose={() => setIsViewingDetails(false)}
						timezone={timezone}
					/>
				</DropOver>
			)}
		</>
	);
};
Event.displayName = "Event";

function getCardStyle(kind: NedapOnsEventKind): WeekViewCardStyle | undefined {
	switch (kind)
	{
		case NedapOnsEventKind.Illness:
		case NedapOnsEventKind.Holidays:
		case NedapOnsEventKind.Unavailable:
		case NedapOnsEventKind.LegalHolidays:
			return "warning";
		default:
			return undefined;
	}
}

export function createNedapOnsMySchedule(props: NedapOnsMyScheduleProps): ReactElement {
	return <NedapOnsMySchedule {...props} />;
};

function getEventKey(item: NedapOnsEventListItem, index: number): number | string {
	return item.kind === NedapOnsEventKind.Shift
		? item.id
		: `index_${index}`;
}

function startDateTimeRetriever(item: NedapOnsEventListItem): string {
	return item.start;
}

function endDateTimeRetriever(item: NedapOnsEventListItem): string {
	return item.end;
}
